import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserPlus, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { CRMIcon } from '../ui';
import crmService from '../../services/crmService';
import '../CRMProfileScreen.css';

const CRMContactManagementTab = ({ profileData, onDataChange, isNewProfile }) => {
    const [formData, setFormData] = useState({
        contact_profiles: [],
        ...profileData
    });
    const [showHRModal, setShowHRModal] = useState(false);
    const [hrEmployees, setHREmployees] = useState([]);
    const [hrLoading, setHRLoading] = useState(false);
    const [hrMessage, setHRMessage] = useState('');
    const [selectedHREmployees, setSelectedHREmployees] = useState([]);
    const [hrImporting, setHRImporting] = useState(false);

    useEffect(() => {
        if (profileData) {
            console.log('🔄 ContactManagementTab: profileData updated, refreshing formData');
            
            setFormData({
                contact_profiles: [],
                ...profileData
            });
        }
    }, [profileData]);

    const handleInputChange = (field, value) => {
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        onDataChange(field, updatedData[field]);
    };

    // Contact Management Functions
    const addContact = () => {
        const currentContacts = formData.contact_profiles || [];
        const newContact = {
            id: Date.now(),
            contact_full_name: '',
            contact_role_title: '',
            contact_department: '',
            direct_phone_number: '',
            mobile_phone_number: '',
            email_address: '',
            additional_notes: '',
            is_primary_contact: currentContacts.length === 0 // First contact is primary by default
        };
        handleInputChange('contact_profiles', [...currentContacts, newContact]);
    };

    const removeContact = (contactId) => {
        const currentContacts = formData.contact_profiles || [];
        const contactToRemove = currentContacts.find(contact => contact.id === contactId);
        let updatedContacts = currentContacts.filter(contact => contact.id !== contactId);
        
        // If removing primary contact and there are other contacts, make first one primary
        if (contactToRemove?.is_primary_contact && updatedContacts.length > 0) {
            updatedContacts[0].is_primary_contact = true;
        }
        
        handleInputChange('contact_profiles', updatedContacts);
    };

    const handleContactChange = (contactId, field, value) => {
        const currentContacts = formData.contact_profiles || [];
        const updatedContacts = currentContacts.map(contact => 
            contact.id === contactId ? { ...contact, [field]: value } : contact
        );
        handleInputChange('contact_profiles', updatedContacts);
    };

    const setPrimaryContact = (contactId) => {
        const currentContacts = formData.contact_profiles || [];
        const updatedContacts = currentContacts.map(contact => ({
            ...contact,
            is_primary_contact: contact.id === contactId
        }));
        handleInputChange('contact_profiles', updatedContacts);
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        // Simple phone validation: optional +, no letters, can start with 0
        const phoneRegex = /^[\+]?[0-9\s\-\(\)]*$/;
        return phone === '' || phoneRegex.test(phone);
    };

    const getContactInputClass = (contact, field) => {
        let baseClass = 'crm-input';
        
        if (field === 'email_address' && contact.email_address && !validateEmail(contact.email_address)) {
            baseClass += ' crm-input-error';
        }
        
        if ((field === 'direct_phone_number' || field === 'mobile_phone_number') && 
            contact[field] && !validatePhone(contact[field])) {
            baseClass += ' crm-input-error';
        }
        
        return baseClass;
    };

    // HR Import functions
    const isHMSEntity = profileData?.is_group_entity === true;

    const openHRImportModal = async () => {
        if (!profileData?.id) return;
        setShowHRModal(true);
        setHRLoading(true);
        setHRMessage('');
        setSelectedHREmployees([]);
        try {
            const result = await crmService.getHRContacts(profileData.id);
            if (result.success) {
                setHREmployees(result.employees || []);
                if (result.message) setHRMessage(result.message);
            } else {
                setHRMessage(result.error || 'Failed to load employees.');
            }
        } catch (err) {
            setHRMessage('Failed to load HR employees.');
        } finally {
            setHRLoading(false);
        }
    };

    const toggleHREmployee = (empId) => {
        setSelectedHREmployees(prev =>
            prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
        );
    };

    const handleHRImport = async () => {
        if (selectedHREmployees.length === 0 || !profileData?.id) return;
        setHRImporting(true);
        try {
            const result = await crmService.importHRContacts(profileData.id, selectedHREmployees);
            if (result.success) {
                setHRMessage(`Imported ${result.imported_count} contact(s). ${result.skipped_count > 0 ? `${result.skipped_count} already existed.` : ''}`);
                // Refresh the page by refetching the profile
                const refreshed = await crmService.getProfile(profileData.id);
                if (refreshed?.contact_profiles) {
                    handleInputChange('contact_profiles', refreshed.contact_profiles);
                }
                setSelectedHREmployees([]);
                // Mark imported employees
                setHREmployees(prev => prev.map(emp =>
                    selectedHREmployees.includes(emp.id) ? { ...emp, already_imported: true } : emp
                ));
            } else {
                setHRMessage(result.error || 'Import failed.');
            }
        } catch (err) {
            setHRMessage('Import failed. Please try again.');
        } finally {
            setHRImporting(false);
        }
    };

    return (
        <div className="crm-tab-panel">
            <div className="crm-form-section">
                <div className="crm-multi-profile-container">
                    <div className="crm-multi-profile-header">
                        <h2 className="crm-multi-profile-title">Contact Management</h2>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            {isHMSEntity && !isNewProfile && profileData?.id && (
                                <button
                                    type="button"
                                    className="crm-add-profile-btn"
                                    onClick={openHRImportModal}
                                    style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}
                                    title="Import contacts from HR system for this legal entity"
                                >
                                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.35rem' }} />
                                    Import from HR
                                </button>
                            )}
                            <button
                                type="button"
                                className="crm-add-profile-btn"
                                onClick={addContact}
                            >
                                <span>+</span>
                                Add New Contact
                            </button>
                        </div>
                    </div>

                    {formData.contact_profiles?.length === 0 && (
                        <div className="crm-empty-state">
                            <div className="crm-empty-icon">
                                <CRMIcon name="contact-management" size="2x" />
                            </div>
                            <h3>No contacts added yet</h3>
                            <p>Add contacts to manage your partner's key personnel and communication channels.</p>
                        </div>
                    )}

                    {formData.contact_profiles?.map((contact, index) => (
                        <div key={contact.id} className="crm-profile-block">
                            <div className="crm-profile-block-header">
                                <div className="crm-contact-title-section">
                                    <h3 className="crm-profile-block-title">
                                        Contact {index + 1}
                                        {contact.is_primary_contact && (
                                            <span className="crm-primary-badge">Primary</span>
                                        )}
                                        {contact.source === 'hr_import' && (
                                            <span style={{
                                                padding: '0.15rem 0.4rem',
                                                backgroundColor: '#d1fae5',
                                                color: '#065f46',
                                                borderRadius: '4px',
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                marginLeft: '0.5rem'
                                            }}>HR</span>
                                        )}
                                    </h3>
                                    {contact.contact_full_name && (
                                        <span className="crm-contact-name-preview">
                                            {contact.contact_full_name}
                                        </span>
                                    )}
                                </div>
                                <div className="crm-profile-block-actions">
                                    {!contact.is_primary_contact && (
                                        <button
                                            type="button"
                                            className="crm-btn-ghost crm-primary-btn"
                                            onClick={() => setPrimaryContact(contact.id)}
                                            title="Set as primary contact"
                                        >
                                            <CRMIcon name="check" /> Set Primary
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="crm-btn-danger crm-remove-btn"
                                        onClick={() => removeContact(contact.id)}
                                        title="Remove contact"
                                    >
                                        <CRMIcon name="close" />
                                    </button>
                                </div>
                            </div>

                            {/* Contact Information Form */}
                            <div className="crm-contact-form">
                                {/* Name, Role, and Department - 3 Column Grid */}
                                <div className="crm-form-grid-3">
                                    <div className="crm-input-group">
                                        <label className="crm-input-label required">Contact Name</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={contact.contact_full_name}
                                            onChange={(e) => handleContactChange(contact.id, 'contact_full_name', e.target.value)}
                                            placeholder="Full name"
                                        />
                                    </div>
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Role/Title</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={contact.contact_role_title}
                                            onChange={(e) => handleContactChange(contact.id, 'contact_role_title', e.target.value)}
                                            placeholder="Title"
                                        />
                                    </div>
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Department</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={contact.contact_department}
                                            onChange={(e) => handleContactChange(contact.id, 'contact_department', e.target.value)}
                                            placeholder="Department"
                                        />
                                    </div>
                                </div>

                                {/* Phone Numbers and Email - 3 Column Grid */}
                                <div className="crm-form-grid-3">
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Direct Phone</label>
                                        <input
                                            type="tel"
                                            className={getContactInputClass(contact, 'direct_phone_number')}
                                            value={contact.direct_phone_number}
                                            onChange={(e) => handleContactChange(contact.id, 'direct_phone_number', e.target.value)}
                                            placeholder="+1 234 567 8900"
                                        />
                                        {contact.direct_phone_number && !validatePhone(contact.direct_phone_number) && (
                                            <span className="crm-input-error-text">Invalid phone format</span>
                                        )}
                                    </div>
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Mobile Phone</label>
                                        <input
                                            type="tel"
                                            className={getContactInputClass(contact, 'mobile_phone_number')}
                                            value={contact.mobile_phone_number}
                                            onChange={(e) => handleContactChange(contact.id, 'mobile_phone_number', e.target.value)}
                                            placeholder="+1 234 567 8901"
                                        />
                                        {contact.mobile_phone_number && !validatePhone(contact.mobile_phone_number) && (
                                            <span className="crm-input-error-text">Invalid phone format</span>
                                        )}
                                    </div>
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Email Address</label>
                                        <input
                                            type="email"
                                            className={getContactInputClass(contact, 'email_address')}
                                            value={contact.email_address}
                                            onChange={(e) => handleContactChange(contact.id, 'email_address', e.target.value)}
                                            placeholder="contact@company.com"
                                        />
                                        {contact.email_address && !validateEmail(contact.email_address) && (
                                            <span className="crm-input-error-text">Invalid email format</span>
                                        )}
                                    </div>
                                </div>

                                {/* Additional Notes Row */}
                                <div className="crm-input-row">
                                    <div className="crm-input-group">
                                        <label className="crm-input-label">Additional Notes</label>
                                        <textarea
                                            className="crm-textarea"
                                            rows={2}
                                            value={contact.additional_notes}
                                            onChange={(e) => handleContactChange(contact.id, 'additional_notes', e.target.value)}
                                            placeholder="Any additional information about this contact..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* HR Import Modal */}
            {showHRModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }} onClick={(e) => { if (e.target === e.currentTarget) setShowHRModal(false); }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem',
                        width: '600px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                            <h3 style={{ margin: 0, color: '#059669', fontWeight: '600' }}>
                                <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.5rem' }} />
                                Import Contacts from HR
                            </h3>
                            <button onClick={() => setShowHRModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        {hrLoading ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading employees...</div>
                        ) : hrEmployees.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                                {hrMessage || 'No employees found for this entity.'}
                            </div>
                        ) : (
                            <>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                                    Select employees to import as CRM contacts for this entity.
                                </p>
                                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                    {hrEmployees.map(emp => (
                                        <div
                                            key={emp.id}
                                            onClick={() => !emp.already_imported && toggleHREmployee(emp.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6',
                                                cursor: emp.already_imported ? 'not-allowed' : 'pointer',
                                                backgroundColor: selectedHREmployees.includes(emp.id) ? '#ecfdf5' : emp.already_imported ? '#f9fafb' : '#fff',
                                                opacity: emp.already_imported ? 0.6 : 1,
                                                transition: 'background-color 0.15s'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedHREmployees.includes(emp.id)}
                                                disabled={emp.already_imported}
                                                onChange={() => {}}
                                                style={{ accentColor: '#059669' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{emp.full_name}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                    {[emp.position, emp.department, emp.work_email].filter(Boolean).join(' | ')}
                                                </div>
                                            </div>
                                            {emp.already_imported && (
                                                <span style={{ fontSize: '0.7rem', color: '#059669', fontWeight: '600', padding: '0.15rem 0.4rem', backgroundColor: '#d1fae5', borderRadius: '4px' }}>
                                                    <FontAwesomeIcon icon={faCheck} /> Imported
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {hrMessage && (
                                    <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', backgroundColor: '#ecfdf5', borderRadius: '6px', fontSize: '0.8rem', color: '#065f46' }}>
                                        {hrMessage}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                                    <button onClick={() => setShowHRModal(false)} style={{ padding: '0.6rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontWeight: '500', fontSize: '0.85rem' }}>
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleHRImport}
                                        disabled={selectedHREmployees.length === 0 || hrImporting}
                                        style={{
                                            padding: '0.6rem 1.25rem', border: 'none', borderRadius: '8px',
                                            background: selectedHREmployees.length === 0 ? '#d1d5db' : 'linear-gradient(135deg, #059669, #047857)',
                                            color: '#fff', cursor: selectedHREmployees.length === 0 ? 'not-allowed' : 'pointer',
                                            fontWeight: '600', fontSize: '0.85rem'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: '0.35rem' }} />
                                        {hrImporting ? 'Importing...' : `Import ${selectedHREmployees.length} Contact(s)`}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CRMContactManagementTab; 